import {
  faCheck,
  faClockFour,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import axiosInstance from "../../utils/axios-instance";
import UseGraph from "../../utils/use-graph";
import formQueryUrl from "../../utils/form-query-url";

// Sequential indigo ramp: darkest = highest balance band
const statusData = [
  {
    icon: faClockFour,
    textColor: "#3730A3",
    bgColor: "bg-[#3730A3]",
    label: "Over £500",
    totalChases: 0,
  },
  {
    icon: faClockFour,
    textColor: "#4F46E5",
    bgColor: "bg-[#4F46E5]",
    label: "£400-£500",
    totalChases: 0,
  },
  {
    icon: faCheck,
    textColor: "#6366F1",
    bgColor: "bg-[#6366F1]",
    label: "£300-£400",
    totalChases: 0,
  },
  {
    icon: faTimesCircle,
    textColor: "#818CF8",
    bgColor: "bg-[#818CF8]",
    label: "£200-£300",
    totalChases: 0,
  },
  {
    icon: faTimesCircle,
    textColor: "#A5B4FC",
    bgColor: "bg-[#A5B4FC]",
    label: "£100-£200",
    totalChases: 0,
  },
  {
    icon: faTimesCircle,
    textColor: "#C7D2FE",
    bgColor: "bg-[#C7D2FE]",
    label: "£0-£100",
    totalChases: 0,
  },
];

const NewPieChartComponent = () => {
  const [data, setData] = useState([]);
  const { startDate, endDate, isRestarted } = UseGraph();

  const totalChases = data.reduce(
    (sum, entry) => sum + (Number(entry?.totalChases) || 0),
    0,
  );

  useEffect(() => {
    const GetData = async () => {
      try {
        const filtersArray = [
          isRestarted && { "restarted": true },
          startDate !== null && { startDate },
          endDate !== null && { endDate },
        ];
        const res = await axiosInstance.get(
          `/stats/collection${formQueryUrl(filtersArray, "?")}`
        );
        const newData = statusData.map((statData) => {
          const newStat = res.data.data.find(
            (item) => item.amountRange === statData.label
          );
          return { ...statData, totalChases: newStat?.totalChases || 0 };
        });
        setData(newData);
      } catch (error) {
        console.log(error);
      }
    };

    GetData();
  }, [startDate, endDate, isRestarted]);

  return (
    <div className="flex flex-col rounded-2xl border border-edge shadow-card w-full h-full px-6 py-5 bg-surface">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[19px] text-heading-color font-semibold tracking-tight">
            Chase Values
          </h1>
          <p className="text-[13px] text-light-gray font-medium mt-1">
            Outstanding amount band
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[19px] text-heading-color font-bold tracking-tight tabular-nums">
            {totalChases}
          </p>
          <p className="text-[13px] text-light-gray font-medium mt-1">chases</p>
        </div>
      </div>

      <div className="relative w-full flex-1 min-h-[170px] mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius="62%"
              outerRadius="92%"
              paddingAngle={2}
              cornerRadius={4}
              stroke="none"
              fill="#8884d8"
              dataKey="totalChases"
              labelLine={false}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry?.textColor} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <p className="text-[26px] font-bold text-heading-color tracking-tight tabular-nums leading-none">
            {totalChases}
          </p>
          <p className="text-[12px] text-light-gray font-medium mt-1.5">
            chases
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-0 w-full mt-4">
        {statusData.map((status, index) => {
          const value = data[index]?.totalChases || 0;
          const share = totalChases > 0 ? (value / totalChases) * 100 : 0;

          return (
            <div key={index} className="flex items-center gap-3 py-[7px]">
              <div
                className={`w-2.5 h-2.5 shrink-0 ${status.bgColor} rounded-full`}
              />

              <p className="text-[13px] text-light-gray font-medium text-nowrap w-[74px] shrink-0">
                {status.label}
              </p>

              <div className="flex-1 h-1.5 rounded-full bg-overlay-mid overflow-hidden min-w-0">
                <div
                  className={`h-full rounded-full transition-[width] duration-500 ${status.bgColor}`}
                  style={{ width: `${Math.min(share, 100)}%` }}
                />
              </div>

              <p className="text-[14px] text-heading-color font-semibold tabular-nums shrink-0">
                {value}
              </p>
              <p className="text-[12px] text-light-gray font-medium tabular-nums w-[32px] text-right shrink-0">
                {Math.round(share)}%
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NewPieChartComponent;
